import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
    Menu, X, Home, Building2, Users, DollarSign, Zap, Receipt, Wrench,
    BarChart3, Settings, Plug, User, Search, ChevronDown, ChevronsLeft, ChevronsRight,
    UserCheck, Briefcase, FileText, ClipboardList, LogOut, Command, Layers, UserCog, Inbox,
} from 'lucide-react';
import NotificationBell from './NotificationBell';

const NAV_SECTIONS = [
    { heading: null, items: [{ name: 'Dashboard', href: '/dashboard', icon: Home }] },
    {
        heading: 'Portfolio',
        items: [
            { name: 'Buildings',   href: '/buildings',    icon: Layers,    roles: ['admin','manager','landlord'] },
            { name: 'Properties',  href: '/properties',   icon: Building2, roles: ['admin','manager','landlord'] },
            { name: 'Tenants',     href: '/tenants',      icon: Users,     roles: ['admin','manager','landlord'] },
            { name: 'Leads',       href: '/leads',        icon: UserCheck, roles: ['admin','manager','landlord'] },
            { name: 'Owners',      href: '/owners',       icon: UserCog,   roles: ['admin','manager','landlord'] },
        ],
    },
    {
        heading: 'Finance',
        items: [
            { name: 'Rent',              href: '/rent',              icon: DollarSign, roles: ['admin','manager','landlord'] },
            { name: 'Invoices',          href: '/invoices',          icon: Receipt,    roles: ['admin','manager','landlord'] },
            { name: 'Expenses',          href: '/expenses',          icon: Receipt,    roles: ['admin','manager','landlord'] },
            { name: 'Unallocated',       href: '/unallocated-payments', icon: Inbox, roles: ['admin','manager','landlord'], badgeKey: 'unallocated' },
            { name: 'Utilities',         href: '/utilities',         icon: Zap,        roles: ['admin','manager','landlord'] },
            { name: 'Owner statements',  href: '/owner-statements',  icon: FileText,   roles: ['admin','manager','landlord','owner'] },
        ],
    },
    {
        heading: 'Operations',
        items: [
            { name: 'Maintenance', href: '/maintenance', icon: Wrench,    roles: ['admin','manager','landlord'] },
            { name: 'Vendors',     href: '/vendors',     icon: Briefcase, roles: ['admin','manager','landlord'] },
            { name: 'Reports',     href: '/reports',     icon: BarChart3, roles: ['admin','manager','landlord'] },
            { name: 'Integrations',href: '/integrations',icon: Plug,      roles: ['admin','manager','landlord'] },
        ],
    },
    {
        heading: 'My account',
        items: [
            { name: 'My statement',  href: '/portal/statement',   icon: FileText,      roles: ['tenant'] },
            { name: 'My lease',      href: '/portal/lease',       icon: ClipboardList, roles: ['tenant'] },
            { name: 'Maintenance',   href: '/portal/maintenance', icon: Wrench,        roles: ['tenant'] },
        ],
    },
];

const Layout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem('nav_collapsed') === '1'; } catch { return false; }
    });
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => { try { localStorage.setItem('nav_collapsed', collapsed ? '1' : '0'); } catch {} }, [collapsed]);
    useEffect(() => { setUserMenuOpen(false); setSidebarOpen(false); }, [location.pathname]);

    const handleLogout = async () => { await logout(); navigate('/login'); };
    const canShow = i => !i.roles || i.roles.includes(user?.user_level);
    // Money received for a unit with no active tenant is invisible unless someone
    // thinks to look. Surface the count wherever the operator happens to be.
    const [badges, setBadges] = useState({ unallocated: 0 });
    useEffect(() => {
        if (!['admin', 'manager', 'landlord'].includes(user?.user_level)) return undefined;
        let alive = true;
        const load = () => axios.get('/api/unallocated-payments')
            .then(r => { if (alive) setBadges(b => ({ ...b, unallocated: r.data?.summary?.count || 0 })); })
            .catch(() => { /* a nav badge is never worth surfacing an error for */ });
        load();
        const t = setInterval(load, 60000);
        return () => { alive = false; clearInterval(t); };
    }, [user]);

    const currentTitle = React.useMemo(() => {
        for (const sec of NAV_SECTIONS) {
            for (const it of sec.items) if (location.pathname === it.href || location.pathname.startsWith(it.href + '/')) return it.name;
        }
        const seg = location.pathname.split('/').filter(Boolean).slice(-1)[0];
        return seg ? seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, ' ') : '';
    }, [location.pathname]);

    const SidebarContent = ({ onNav }) => (
        <>
            <div className={`flex h-16 items-center border-b border-neutral-200/70 px-4 ${collapsed ? 'justify-center' : 'justify-between'}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary-600 to-primary-800 text-white flex items-center justify-center flex-shrink-0 shadow-sm shadow-primary-700/25 ring-1 ring-primary-700/20">
                        <Building2 className="h-4 w-4" />
                    </div>
                    {!collapsed && (
                        <div className="flex flex-col min-w-0">
                            <span className="text-sm font-bold text-neutral-900 tracking-tight truncate leading-tight">PropertyMS</span>
                            <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wider">Enterprise</span>
                        </div>
                    )}
                </div>
                {!collapsed && (
                    <button onClick={() => setCollapsed(true)}
                            className="hidden lg:inline-flex text-neutral-400 hover:text-neutral-600 p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
                            title="Collapse sidebar">
                        <ChevronsLeft className="h-4 w-4" />
                    </button>
                )}
            </div>

            <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-4">
                {NAV_SECTIONS.map((sec, si) => {
                    const items = sec.items.filter(canShow);
                    if (items.length === 0) return null;
                    return (
                        <div key={si}>
                            {sec.heading && !collapsed && (
                                <div className="px-3 mb-1.5 text-[10px] font-bold text-neutral-400 uppercase tracking-wider">
                                    {sec.heading}
                                </div>
                            )}
                            <div className="space-y-1">
                                {items.map(item => {
                                    const active = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                                    return (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            onClick={onNav}
                                            title={collapsed ? item.name : undefined}
                                            className={`group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150
                                                        ${collapsed ? 'justify-center' : ''}
                                                        ${active
                                                            ? 'bg-primary-50/80 text-primary-900 font-semibold shadow-xs border border-primary-100/90'
                                                            : 'text-neutral-600 hover:bg-neutral-100/80 hover:text-neutral-900'}`}>
                                            {active && (
                                                <span className="absolute left-1 top-2 bottom-2 w-1 bg-primary-700 rounded-full shadow-glow" />
                                            )}
                                            <item.icon className={`h-4 w-4 flex-shrink-0 transition-transform duration-150 group-hover:scale-110 ${active ? 'text-primary-700' : 'text-neutral-400 group-hover:text-neutral-600'}`} />
                                            {!collapsed && <span className="truncate">{item.name}</span>}
                                            {item.badgeKey && badges[item.badgeKey] > 0 && (collapsed
                                                ? <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-500" />
                                                : <span className="ml-auto min-w-[1.25rem] px-1.5 py-0.5 text-[10px] font-bold text-center
                                                                   text-amber-900 bg-amber-100 border border-amber-300 rounded-full">
                                                    {badges[item.badgeKey]}
                                                  </span>)}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                <div className="pt-2 border-t border-neutral-100">
                    <Link to="/settings" onClick={onNav}
                          className={`group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium
                                      text-neutral-600 hover:bg-neutral-100/80 hover:text-neutral-900 transition-colors
                                      ${collapsed ? 'justify-center' : ''}`}>
                        <Settings className="h-4 w-4 text-neutral-400 group-hover:text-neutral-600 flex-shrink-0 transition-transform duration-150 group-hover:scale-110" />
                        {!collapsed && <span>Settings</span>}
                    </Link>
                </div>
            </nav>

            {collapsed && (
                <div className="p-2 border-t border-neutral-200/70 hidden lg:block">
                    <button onClick={() => setCollapsed(false)}
                            className="w-full flex justify-center p-2 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
                            title="Expand sidebar">
                        <ChevronsRight className="h-4 w-4" />
                    </button>
                </div>
            )}
        </>
    );

    const sidebarWidth = collapsed ? 'lg:w-16' : 'lg:w-60';
    const contentPad   = collapsed ? 'lg:pl-16' : 'lg:pl-60';

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
                <div className="fixed inset-0 bg-neutral-900/40 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
                <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white shadow-lg animate-slide-up">
                    <div className="absolute right-2 top-2 z-10">
                        <button onClick={() => setSidebarOpen(false)} className="p-2 text-neutral-400 hover:text-neutral-600 rounded-md">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <SidebarContent onNav={() => setSidebarOpen(false)} />
                </div>
            </div>

            <aside className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col ${sidebarWidth} bg-white border-r border-neutral-200/70 transition-all duration-200`}>
                <SidebarContent />
            </aside>

            <div className={contentPad}>
                <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-neutral-200/75 shadow-xs transition-all">
                    <div className="h-full flex items-center justify-between gap-4 px-4 sm:px-6">
                        <div className="flex items-center gap-3 min-w-0">
                            <button onClick={() => setSidebarOpen(true)}
                                    className="lg:hidden p-2 -ml-2 rounded-lg text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors">
                                <Menu className="h-5 w-5" />
                            </button>
                            <h2 className="text-sm sm:text-base font-bold text-neutral-900 tracking-tight truncate">{currentTitle}</h2>
                        </div>

                        <div className="hidden md:flex flex-1 max-w-md">
                            <div className="relative w-full">
                                <Search className="absolute left-3 top-2.5 h-4 w-4 text-neutral-400 pointer-events-none" />
                                <input type="text" placeholder="Jump to property, tenant, or invoice…"
                                       className="w-full pl-9 pr-14 py-2 text-sm bg-neutral-50/80 border border-neutral-200/90 rounded-xl
                                                  placeholder:text-neutral-400 shadow-[inset_0_1px_2px_rgba(0,0,0,0.02)]
                                                  focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all" />
                                <kbd className="absolute right-2.5 top-2 hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono text-neutral-500 bg-white border border-neutral-200/90 rounded-md shadow-2xs">
                                    <Command className="h-2.5 w-2.5" />K
                                </kbd>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <NotificationBell />

                            <div className="relative">
                                <button onClick={() => setUserMenuOpen(o => !o)}
                                        className="flex items-center gap-2.5 p-1 pl-1 pr-2.5 rounded-xl hover:bg-neutral-100/80 transition-colors">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-600 to-primary-800 text-white text-xs font-bold flex items-center justify-center shadow-xs ring-2 ring-white">
                                        {(user?.first_name?.[0] || 'U').toUpperCase()}
                                    </div>
                                    <span className="hidden sm:block text-sm font-semibold text-neutral-800 max-w-[140px] truncate">
                                        {user?.first_name} {user?.last_name}
                                    </span>
                                    <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
                                </button>
                                {userMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-60 card shadow-xl ring-1 ring-black/5 py-1.5 animate-slide-down z-50">
                                        <div className="px-4 py-2.5 border-b border-neutral-100">
                                            <p className="text-sm font-bold text-neutral-900 truncate">
                                                {user?.first_name} {user?.last_name}
                                            </p>
                                            <p className="text-xs text-neutral-500 truncate mt-0.5">{user?.email}</p>
                                            <span className="mt-1.5 inline-block text-[10px] font-bold uppercase tracking-wider text-primary-700 bg-primary-50 px-2 py-0.5 rounded-full border border-primary-200/60">
                                                {user?.user_level}
                                            </span>
                                        </div>
                                        <div className="py-1">
                                            <Link to="/settings" onClick={() => setUserMenuOpen(false)}
                                                  className="flex items-center gap-2.5 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 font-medium transition-colors">
                                                <Settings className="h-4 w-4 text-neutral-400" />
                                                Settings
                                            </Link>
                                            <button onClick={handleLogout}
                                                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-danger-600 hover:bg-danger-50 font-medium transition-colors">
                                                <LogOut className="h-4 w-4" />
                                                Sign out
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default Layout;
